import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/LanguageContext';
import { formatApiError } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { AlertCircle, Loader2, Headphones, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex justify-end mb-2">
          <LanguageSwitcher variant="light" />
        </div>
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#002FA7] text-white mb-4">
            <Headphones className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            IT Helpdesk
          </h1>
          <p className="text-[#71717A] mt-2">{t('auth_tagline')}</p>
        </div>

        <Card className="border border-[#E4E4E7] shadow-none">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('login_title')}
            </CardTitle>
            <CardDescription>
              {t('login_subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md" data-testid="login-error">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nome@esempio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#002FA7]"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white font-medium"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('login_in_progress')}
                  </>
                ) : (
                  t('login_button')
                )}
              </Button>
            </form>

            <div className="mt-4 flex items-center justify-between text-sm">
              <Link to="/forgot-password" className="text-[#002FA7] hover:underline" data-testid="forgot-password-link">
                {t('forgot_password')}
              </Link>
              <span className="text-[#71717A]">
                {t('no_account')}{' '}
                <Link to="/register" className="text-[#002FA7] hover:underline font-medium" data-testid="register-link">
                  {t('sign_up')}
                </Link>
              </span>
            </div>

            <div className="mt-6 text-center">
              <Link to="/support" className="inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline font-medium" data-testid="support-link">
                <Headphones className="w-4 h-4" />
                {t('need_help')}
              </Link>
            </div>

            <div className="mt-6 pt-6 border-t border-[#E4E4E7]">
              <a
                href="https://t.me/ikonet_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-md bg-gradient-to-r from-[#e9f5fb] to-white border border-[#0088cc]/30 hover:shadow-sm transition"
                data-testid="telegram-bot-link"
              >
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=72x72&data=https%3A%2F%2Ft.me%2Fikonet_bot&margin=1"
                  alt="QR Code Telegram @ikonet_bot"
                  className="w-16 h-16 rounded border border-[#E4E4E7]"
                  width="72"
                  height="72"
                  data-testid="telegram-qr-code"
                />
                <div className="text-left flex-1">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#0088cc] text-white">
                      <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
                        <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-[#0F172A]">Bot Telegram IKONET</span>
                  </div>
                  <p className="text-xs text-[#71717A] mt-1">Scansiona il QR o tocca per aprire <span className="text-[#0088cc] font-medium">@ikonet_bot</span></p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-[#71717A] mt-6">
          {t('powered_by')}
        </p>
      </div>
    </div>
  );
}
